// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Dropdown, TextInput, TextArea, useToast } from '@loa/ui-components';
import axios from 'axios';
import { proposalApi } from '../../api/proposalApi';
import {
  COVER_TYPE_OPTIONS,
  PREMIUM_FREQUENCY_OPTIONS,
  PROTECTION_TYPE_OPTIONS,
  type Proposal,
} from '../../types/proposal';
import type { Client } from '../../types/client';
import { useSoloClient } from '../../hooks/useSoloClient';

type Props = Readonly<{
  open: boolean;
  onClose: () => void;
  clients: Client[];
  brokerageId: string;
  profileId: string;
  onCreated?: (proposal: Proposal) => void;
  existingProposal?: Proposal;
  onUpdated?: (proposal: Proposal) => void;
}>;

/** Compose (or edit) a PROTECTION proposal. */
export const NewProtectionProposalModal: React.FC<Props> = ({
  open,
  onClose,
  clients,
  profileId,
  onCreated,
  existingProposal,
  onUpdated,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const isEditMode = Boolean(existingProposal);
  const detail = (existingProposal?.details ?? {}) as Record<string, unknown>;
  const { isSolo, soloClient } = useSoloClient(clients);

  const [targetClientId, setTargetClientId] = useState(existingProposal?.targetClientId ?? '');

  // Solo relationships (Investment & Protection Proposals PRD RQ-9) never
  // show the Client selector — auto-select the only possible recipient.
  useEffect(() => {
    if (!isEditMode && !targetClientId && isSolo && soloClient) {
      setTargetClientId(soloClient.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, isSolo, soloClient]);
  const [title, setTitle] = useState(existingProposal?.title ?? '');
  const [clientMessage, setClientMessage] = useState(existingProposal?.clientMessage ?? '');
  const [validUntil, setValidUntil] = useState(existingProposal?.validUntil ?? '');
  const [protectionType, setProtectionType] = useState(String(detail.protectionType ?? ''));
  const [coverType, setCoverType] = useState(String(detail.coverType ?? 'SINGLE'));
  const [sumAssured, setSumAssured] = useState(detail.sumAssured != null ? String(detail.sumAssured) : '');
  const [termYears, setTermYears] = useState(detail.termYears != null ? String(detail.termYears) : '');
  const [premiumAmount, setPremiumAmount] = useState(
    detail.premiumAmount != null ? String(detail.premiumAmount) : '',
  );
  const [premiumFrequency, setPremiumFrequency] = useState(String(detail.premiumFrequency ?? 'MONTHLY'));
  const [rationale, setRationale] = useState(String(detail.rationale ?? ''));
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    (isEditMode || Boolean(targetClientId)) &&
    Boolean(protectionType) &&
    Boolean(coverType) &&
    Boolean(sumAssured) &&
    Boolean(termYears) &&
    Boolean(premiumAmount) &&
    Boolean(premiumFrequency);

  const buildDetails = (): Record<string, unknown> => ({
    protectionType,
    coverType,
    sumAssured: Number(sumAssured),
    currencyCode: 'EUR',
    termYears: Number(termYears),
    premiumAmount: Number(premiumAmount),
    premiumFrequency,
    rationale: rationale || undefined,
  });

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if (isEditMode && existingProposal) {
        const updated = await proposalApi.updateProposal(existingProposal.id, {
          title: title || undefined,
          clientMessage: clientMessage || undefined,
          validUntil: validUntil || undefined,
          details: buildDetails(),
        });
        toast.showToast('Proposal updated', 'success');
        onUpdated?.(updated);
        onClose();
      } else {
        const created = await proposalApi.createProposal({
          profileId,
          targetClientId,
          proposalType: 'PROTECTION',
          title: title || undefined,
          clientMessage: clientMessage || undefined,
          validUntil: validUntil || undefined,
          details: buildDetails(),
        });
        toast.showToast('Proposal created', 'success');
        onCreated?.(created);
        navigate(`/relationships/${profileId}/proposals/${created.id}`);
      }
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string })?.message ?? err.message
        : 'Failed to save proposal';
      toast.showToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const clientOptions = clients.map((c) => ({ value: c.id, text: `${c.firstName} ${c.lastName}` }));
  const protectionTypeOptions = PROTECTION_TYPE_OPTIONS.map((o) => ({ value: o.value, text: o.label }));
  const coverTypeOptions = COVER_TYPE_OPTIONS.map((o) => ({ value: o.value, text: o.label }));
  const premiumFrequencyOptions = PREMIUM_FREQUENCY_OPTIONS.map((o) => ({ value: o.value, text: o.label }));

  return (
    <Modal open={open} onClose={onClose} size="large">
      <Modal.Header
        icon={isEditMode ? 'edit' : 'plus square outline'}
        title={isEditMode ? 'Edit Protection Proposal' : 'New Protection Proposal'}
        onClose={onClose}
      />
      <Modal.Content>
        <div data-testid="protection-proposal-form" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!isEditMode && !isSolo && (
            <Dropdown label="Client" placeholder="Select a client" options={clientOptions}
              value={targetClientId} onChange={setTargetClientId} aria-label="Client" />
          )}
          <TextInput label="Title" value={title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)} />
          <Dropdown label="Protection type" placeholder="Select a type" options={protectionTypeOptions}
            value={protectionType} onChange={setProtectionType} aria-label="Protection type" />
          <Dropdown label="Cover type" options={coverTypeOptions}
            value={coverType} onChange={setCoverType} aria-label="Cover type" />
          <TextInput label="Sum assured (EUR)" type="number" value={sumAssured}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSumAssured(e.target.value)} />
          <TextInput label="Term (years)" type="number" value={termYears}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTermYears(e.target.value)} />
          <TextInput label="Premium (EUR)" type="number" value={premiumAmount}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPremiumAmount(e.target.value)} />
          <Dropdown label="Premium frequency" options={premiumFrequencyOptions}
            value={premiumFrequency} onChange={setPremiumFrequency} aria-label="Premium frequency" />
          <TextInput label="Valid until (optional)" type="date" value={validUntil}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValidUntil(e.target.value)} />
          <TextArea label="Message to client" value={clientMessage}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setClientMessage(e.target.value)} rows={3} />
          <TextArea label="Rationale" value={rationale}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRationale(e.target.value)} rows={3} />
        </div>
      </Modal.Content>
      <Modal.Actions
        primaryAction={{
          label: isEditMode ? 'Save changes' : 'Create proposal',
          onClick: handleSubmit,
          disabled: !canSubmit || submitting,
          loading: submitting,
        }}
        secondaryAction={{ label: 'Cancel', onClick: onClose }}
      />
    </Modal>
  );
};

export default NewProtectionProposalModal;
